import { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';

/**
 * Which entity's book the Financials screens are showing this person.
 *
 * One request per page load, shared: the scope notice and the empty state both
 * need the same answer, and asking twice for it on every list was the obvious
 * next bug. The in-flight promise is cached at module level, so N consumers on
 * one screen cost one call.
 *
 * Returns `null` until the answer arrives, then:
 *   { unresolved, warning, entityName, allEntities }
 */

let pending = null;

export const fetchFinancialScope = () => {
  if (!pending) {
    pending = axiosInstance
      .get('/api/dashboard/user-info/')
      .then(({ data }) => ({
        unresolved: Boolean(data?.financial_scope_unresolved),
        warning: data?.financial_scope_warning || null,
        entityName: data?.financial_entity_name || null,
        allEntities: Boolean(data?.can_access_all_entities),
      }))
      .catch((error) => {
        // Never block a Financials screen on this. A missing notice is a much
        // smaller problem than a list that will not render.
        console.error('Could not read the Financials entity scope:', error);
        pending = null;   // let the next screen try again
        return null;
      });
  }
  return pending;
};

/** Test seam: drop the cached answer between renders. */
export const resetFinancialScopeCache = () => { pending = null; };

const useFinancialScope = () => {
  const [scope, setScope] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchFinancialScope().then((result) => {
      if (!cancelled) setScope(result);
    });
    return () => { cancelled = true; };
  }, []);

  return scope;
};

/** True when this person is being shown one entity and the rest is hidden. */
export const scopeIsNarrowing = (scope) =>
  Boolean(scope && !scope.allEntities && !scope.unresolved && scope.entityName);

export default useFinancialScope;
